import type { WizardData } from '../types/wizard';

export type WizardLanguage = 'pt' | 'en';
export const TOTAL_WIZARD_STEPS = 10;

export interface AuthenticatedCustomerIdentity {
  email?: string | null;
  name?: string | null;
}

export function getNextWizardStep(step: number): number {
  return Math.min(Math.max(Math.trunc(step), 1) + 1, TOTAL_WIZARD_STEPS);
}

export function getPreviousWizardStep(step: number): number {
  return Math.max(Math.min(Math.trunc(step), TOTAL_WIZARD_STEPS) - 1, 1);
}

export function applyDestinationMode(
  data: WizardData,
  destinationMode: NonNullable<WizardData['destinationMode']>,
): WizardData {
  return {
    ...data,
    destinationMode,
    destinationSuggestionChoice: '',
    destinationSuggestionCountry: '',
    destinationSuggestionFitAngle: '',
    destinationSuggestionReason: '',
    destinationSuggestionHighlights: [],
  };
}

export function applyAuthenticatedCustomerPrefill(
  data: WizardData,
  identity: AuthenticatedCustomerIdentity,
): WizardData {
  return {
    ...data,
    email: data.email?.trim() ? data.email : identity.email?.trim() ?? '',
    fullName: data.fullName?.trim() ? data.fullName : identity.name?.trim() ?? '',
  };
}

export function applyOptionalStepSkip(data: WizardData, step: number): WizardData {
  if (step === 6) return { ...data, previousTrips: [] };
  if (step === 8) return { ...data, avoidExperiences: [], avoidanceStatus: 'none' };
  if (step === 9) {
    return {
      ...data,
      dietaryRestrictions: [],
      mobilityNeeds: [],
      healthConditions: [],
      specialNeedsNotes: '',
      specialNeedsStatus: 'none',
    };
  }
  return data;
}

export function hasPhoneCountryCode(phone: string): boolean {
  return /^\+\d{1,3}[\s\d().-]{7,}$/.test(phone.trim());
}

export function isLikelyBrazilianPhoneWithoutCountryCode(phone: string): boolean {
  const raw = phone.trim();
  const digits = raw.replace(/\D/g, '');
  if (!digits || raw.startsWith('+') || digits.startsWith('55')) return false;
  return digits.length === 10 || digits.length === 11;
}

export function validateWizardStep(
  step: number,
  data: WizardData,
  language: WizardLanguage = 'pt',
): Record<string, string> {
  const errors: Record<string, string> = {};
  const en = language === 'en';

  if (step === 1) {
    if (!data.ageRange) errors.ageRange = en ? 'Select your age range' : 'Selecione sua faixa etária';
    if (!data.occupation) errors.occupation = en ? 'Select your occupation' : 'Selecione sua ocupação';
    if (!data.lifestyle) errors.lifestyle = en ? 'Select your lifestyle' : 'Selecione seu estilo de vida';
  }
  if (step === 2 && !(data.travelStyles?.length || data.travelStyle)) {
    errors.travelStyle = en ? 'Select at least one travel style' : 'Selecione pelo menos um estilo de viagem';
  }
  if (step === 3 && !data.regions?.length) {
    errors.regions = en ? 'Select at least one region' : 'Selecione pelo menos uma região';
  }
  if (step === 4 && !data.rhythm) {
    errors.rhythm = en ? 'Select the travel rhythm' : 'Selecione o ritmo da viagem';
  }
  if (step === 5 && !data.timePreference) {
    errors.timePreference = en ? 'Select your time preference' : 'Selecione sua preferência de horário';
  }
  if (step === 7 && (data.mustDoExperiences?.length ?? 0) < 3) {
    errors.mustDoExperiences = en
      ? 'Select at least 3 must-have experiences'
      : 'Selecione pelo menos 3 experiências obrigatórias';
  }
  if (step !== 10) return errors;

  if (!data.fullName?.trim()) errors.fullName = en ? 'Enter your full name' : 'Informe seu nome completo';
  const email = data.email?.trim() ?? '';
  if (!email) errors.email = en ? 'Enter your email' : 'Informe seu e-mail';
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    errors.email = en ? 'Enter a valid email' : 'Informe um e-mail válido';
  }
  const phone = data.phone?.trim() ?? '';
  if (!phone) errors.phone = en ? 'Enter your WhatsApp number' : 'Informe seu WhatsApp';
  else if (isLikelyBrazilianPhoneWithoutCountryCode(phone) || !hasPhoneCountryCode(phone)) {
    errors.phone = en
      ? 'Include the country code. Example: +55 62 99999-9999'
      : 'Inclua o código do país. Exemplo: +55 62 99999-9999';
  }
  if (!data.origem?.trim()) errors.origem = en ? 'Enter your departure city' : 'Informe a cidade de partida';
  if ((data.destinationMode ?? 'manual') === 'manual') {
    if (!data.destino?.trim()) errors.destino = en ? 'Enter the destination' : 'Informe o destino';
  } else if (!data.destinationSuggestionChoice?.trim()) {
    errors.destino = en ? 'Select a suggestion to continue' : 'Selecione uma sugestão para continuar';
  }
  if (!data.dataInicio) errors.dataInicio = en ? 'Enter the departure date' : 'Informe a data de ida';
  if (!data.dataFim) errors.dataFim = en ? 'Enter the return date' : 'Informe a data de volta';
  if (data.dataInicio && data.dataFim && data.dataFim < data.dataInicio) {
    errors.dataFim = en ? 'Return date must be after departure' : 'A volta deve ser posterior à ida';
  }
  if (!data.orcamento) errors.orcamento = en ? 'Select your budget' : 'Selecione o orçamento';
  return errors;
}

export function canSubmitWizard(data: WizardData, language: WizardLanguage = 'pt'): boolean {
  return Object.keys(validateWizardStep(TOTAL_WIZARD_STEPS, data, language)).length === 0;
}

export function buildRecommendationRequestProfile(data: WizardData) {
  return {
    origin: data.origem ?? '',
    startDate: data.dataInicio ?? '',
    endDate: data.dataFim ?? '',
    ageRange: data.ageRange ?? '',
    occupation: data.occupation ?? '',
    lifestyle: data.lifestyle ?? '',
    travelStyles: data.travelStyles?.length ? data.travelStyles : data.travelStyle ? [data.travelStyle] : [],
    rhythm: data.rhythm ?? '',
    timePreference: data.timePreference ?? '',
    regions: data.regions ?? [],
    budget: data.orcamento ?? '',
    previousTrips: data.previousTrips ?? [],
    mustDoExperiences: data.mustDoExperiences ?? [],
    avoidExperiences: data.avoidExperiences ?? [],
    adults: data.numeroAdultos ?? '1',
    children: data.numeroCriancas ?? '0',
  };
}
